import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { Form, message, Typography } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash-es'
import { useMutation, useQuery } from 'react-query'

import { Cancel, LastSentInfo, Save } from 'components/UI'
import SendPlaneFill from 'assets/images/send-plane-fill.svg?react'
import { optionsSelector } from 'app-redux/selectors'
import { financeOnboardingEvent } from 'app-redux/financeOnboardingSlice'
import {
  sendFinanceBankTransWhatsapp,
  sendFinanceInvoiceWhatsapp,
  sendFinanceOrderWhatsapp,
} from 'utils/apis'
import { transType } from 'utils/static'
import OfficialWhatsapp, {
  useInputWhatsappTemplate,
} from 'components/UI/SendWaModal/OfficialWhatsapp'
import WhatsappLimitModal, { getWhatsappLimitMessage } from 'containers/Billing/WhatsappLimitModal'
import styles from './send-invoice-form.module.scss'
import Methods from './Methods'
import WhatsAppMethod from './WhatsAppMethod'
import EmailMethod from './EmailMethod'
import { getRelatedEmailAttachments } from './relatedEmailAttachments'

const { Text } = Typography

const useWATemplate = ({ enabled, fetcher, key }) => {
  return useQuery(
    [key],
    async () => {
      const {
        data: { data },
      } = await fetcher()
      return data
    },
    { enabled },
  )
}

const useEmailTemplate = ({ enabled, fetcher, key }) => {
  return useQuery(
    [key],
    async () => {
      const {
        data: { data },
      } = await fetcher()
      return data
    },
    { enabled },
  )
}

const useSendWhatsappMutation = ({ transTypeId }) => {
  const fetchFunction = useMemo(() => {
    switch (transTypeId) {
      case transType.RECEIVE_INVOICE_PAYMENT:
        return sendFinanceBankTransWhatsapp
      case transType.ORDER:
        return sendFinanceOrderWhatsapp
      default:
        return sendFinanceInvoiceWhatsapp
    }
  }, [transTypeId])

  return useMutation(fetchFunction)
}

const SendInvoiceForm = ({
  data,
  isMass,
  goToEmailTemplate,
  getEmailTemplate,
  emailTemplateKey,
  getWaTemplate,
  waTemplateKey,
  onClose,
  sendEmail,
  transTypeId,
  onConfirmMassSend,
  onPrintPdf,
  goToWaTamplate,
  withRelationAttachment,
  footerSentVia,
}) => {
  const { formatMessage } = useIntl()
  const dispatch = useDispatch()
  const [form] = Form.useForm()
  const [sendMethods, setSendMethods] = useState(['WhatsApp'])
  const [selectedMethod, setSelectedMethod] = useState('WhatsApp')
  const [loading, setLoading] = useState(false)
  const [advanceWhatsApp, setAdvanceWhatsApp] = useState(false)
  const [whatsappLimitMessage, setWhatsappLimitMessage] = useState('')

  const options = useSelector(optionsSelector)

  const { data: waTemplate, isLoading: isLoadingWaTemplate } = useWATemplate({
    enabled: sendMethods.includes('WhatsApp') && selectedMethod === 'WhatsApp' && !isMass,
    fetcher: getWaTemplate,
    key: [waTemplateKey, transTypeId, data.id],
  })
  const { data: inputWaTemplate, isLoading: isLoadingInputWaTemplate } = useInputWhatsappTemplate({
    id: data.id,
    transTypeId,
    options: { enabled: sendMethods.includes('WhatsApp') && selectedMethod === 'WhatsApp' },
  })
  const {
    data: emailTemplate,
    isSuccess: isSuccessEmailTemplate,
    isLoading: loadingEmailTemplate,
  } = useEmailTemplate({
    enabled: sendMethods.includes('Email') || selectedMethod === 'Email',
    fetcher: getEmailTemplate,
    key: emailTemplateKey,
  })
  const sendWhatsappMutation = useSendWhatsappMutation({ transTypeId })

  const loadingWaTemplate = isLoadingInputWaTemplate || isLoadingWaTemplate

  const formattedEmailTemplate = useMemo(() => {
    if (isEmpty(emailTemplate)) {
      return {}
    }

    const _data = { ...emailTemplate }
    if (!isEmpty(_data.mail_to_all)) {
      _data.mail_to = _data.mail_to_all.filter((mail) => !!mail)
    } else if (!isEmpty(_data.mail_to)) {
      _data.mail_to = [_data.mail_to]
    } else {
      _data.mail_to = []
    }
    return _data
  }, [emailTemplate])

  const emailTemplateData = useMemo(
    () => (emailTemplate ? { ...data, ...emailTemplate } : {}),
    [data, emailTemplate],
  )

  const relatedAttachments = useMemo(
    () => getRelatedEmailAttachments(data?.relations),
    [data?.relations],
  )

  useEffect(() => {
    if (isSuccessEmailTemplate && emailTemplate) {
      form.setFieldsValue({
        mail_to: formattedEmailTemplate.mail_to,
        subject: formattedEmailTemplate.subject || '',
        body: formattedEmailTemplate.body || '',
        mail_from: options.reply_email_address.split(',')[0],
        mail_cc: formattedEmailTemplate.mail_cc,
      })
    }
  }, [
    emailTemplate,
    form,
    formattedEmailTemplate.body,
    formattedEmailTemplate.mail_cc,
    formattedEmailTemplate.mail_to,
    formattedEmailTemplate.subject,
    isSuccessEmailTemplate,
    options.reply_email_address,
  ])

  useEffect(() => {
    if (waTemplate) {
      form.setFieldsValue({
        contact_phone_number: waTemplate?.phone ? `+${waTemplate?.phone}` : '',
      })
    }
  }, [waTemplate, form])

  const handleClose = () => {
    onClose()
    form.resetFields()
    setSendMethods(['whatsapp'])
  }

  const handleSelectedMethodChange = (method) => setSelectedMethod(method)

  const handleSubmit = async () => {
    if (options.demo && sendMethods.includes('Email')) {
      message.error(formatMessage({ id: 'financeInvoices.email_is_not_available_in_demo' }))
      return
    }

    if (isMass && onConfirmMassSend) {
      onConfirmMassSend(sendMethods)
      return
    }

    try {
      setLoading(true)
      await form.validateFields()

      // Untuk get yang hidden juga
      const values = form.getFieldsValue(true)

      if (sendMethods.includes('WhatsApp') && isEmpty(values.contact_phone_number)) {
        message.error(formatMessage({ id: 'sendEmail.phone_number_is_required' }))
        setLoading(false)
        return
      }

      if (sendMethods.includes('Email') && isEmpty(values.mail_to)) {
        message.error(formatMessage({ id: 'sendEmail.email_to_is_required' }))
        setLoading(false)
        return
      }

      if (sendMethods.includes('Email')) {
        const emailPayload = {
          mail_to: Array.isArray(values.mail_to) ? values.mail_to.join(',') : '',
          mail_from: values.mail_from,
          mail_cc: Array.isArray(values.mail_cc) ? values.mail_cc.join(',') : '',
          body: emailTemplate.body,
          subject: emailTemplate.subject,
          include_attachment_order:
            withRelationAttachment && relatedAttachments.order
              ? !!values.include_attachment_order
              : false,
          include_attachment_delivery:
            withRelationAttachment && relatedAttachments.delivery
              ? !!values.include_attachment_delivery
              : false,
          include_attachment_relation: true,
        }
        await sendEmail(data.id, emailPayload)
        message.success(formatMessage({ id: 'financeInvoices.email_sent_successfully' }))
      }
      if (sendMethods.includes('WhatsApp')) {
        await sendWhatsappMutation.mutateAsync({
          id: data.id,
          payload: advanceWhatsApp ? values.official_whatsapp_value_replacement : [],
          contact_phone_number: values.contact_phone_number,
        })
        message.success(formatMessage({ id: 'financeInvoices.whatsapp_sent_successfully' }))
        dispatch(financeOnboardingEvent('whatsapp_sent'))
      }
      setLoading(false)
      handleClose()
    } catch (error) {
      setLoading(false)
      const limitMessage = getWhatsappLimitMessage(error)
      if (limitMessage) {
        setWhatsappLimitMessage(limitMessage)
        return
      }
      console.error('Validation failed:', error)
    }
  }

  const handleCloseWhatsappLimitModal = useCallback(() => setWhatsappLimitMessage(''), [])

  const handleMethodToggle = (method) => {
    setSendMethods((prev) => {
      if (prev.includes(method)) {
        return prev.filter((_method) => _method !== method)
      }
      return [...prev, method]
    })
  }

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        className={styles.formRoot}
        initialValues={{
          // Whatsapp
          contact_phone_number: waTemplate?.phone ? `+${waTemplate?.phone}` : '',
          official_whatsapp_value_replacement: [],

          // Email
          from: options.reply_email_address.split(',')[0],
          mail_to: [],
          mail_cc: [],
          include_attachment_order: true,
          include_attachment_delivery: true,
        }}
      >
        <div className={styles.formWrapper}>
          <div className={styles.modalContent}>
            <Methods
              isMass={isMass}
              methods={sendMethods}
              onMethodToggle={handleMethodToggle}
              onSelectedMethodChange={handleSelectedMethodChange}
              selectedMethod={selectedMethod}
              advanceWhatsApp={advanceWhatsApp}
              setAdvanceWhatsApp={setAdvanceWhatsApp}
            />

            {!isMass && selectedMethod === 'Email' && (
              <LastSentInfo
                dateTime={data.already_send_email}
                messageId="financeInvoices.last_sent_email_on"
                transTypeId={transTypeId}
                tranId={data.id}
              />
            )}
            {!isMass && selectedMethod === 'WhatsApp' && (
              <LastSentInfo
                dateTime={data.already_send_whatsapp}
                messageId="financeInvoices.last_sent_whatsapp_on"
                transTypeId={transTypeId}
                tranId={data.id}
              />
            )}

            <div className={styles.mainSection}>
              {selectedMethod === 'WhatsApp' ? (
                advanceWhatsApp ? (
                  <OfficialWhatsapp id={data.id} transTypeId={transTypeId} usePreview />
                ) : (
                  <WhatsAppMethod
                    data={inputWaTemplate}
                    loading={loadingWaTemplate}
                    isMass={isMass}
                    getWaTemplate={getWaTemplate}
                    goToWaTamplate={goToWaTamplate}
                    transTypeId={transTypeId}
                    waTemplateKey={waTemplateKey}
                    onClose={onClose}
                  />
                )
              ) : null}
              {selectedMethod === 'Email' && (
                <EmailMethod
                  data={emailTemplateData}
                  emailData={emailTemplate}
                  goToEmailTemplate={goToEmailTemplate}
                  isMass={isMass}
                  loading={loadingEmailTemplate}
                  onPrintPdf={onPrintPdf}
                  withRelationAttachment={withRelationAttachment}
                />
              )}
            </div>
          </div>
          <div className={styles.modalFooter}>
            <Text className={styles.modalFooterText}>
              {footerSentVia || formatMessage({ id: 'financeInvoices.invoice_will_be_sent_via' })}:{' '}
              <b>{sendMethods.join(', ')}</b>
            </Text>
            <div className={styles.modalFooterButtons}>
              <Cancel icon={null} onClick={handleClose} className={styles.buttonCancel} />
              <Save
                type="primary"
                icon={<SendPlaneFill />}
                onClick={handleSubmit}
                title={formatMessage({ id: 'financeInvoices.send_now' })}
                loading={loading || sendWhatsappMutation.isLoading}
                disabled={sendMethods.length === 0}
                className={styles.buttonSave}
              />
            </div>
          </div>
        </div>
      </Form>
      <WhatsappLimitModal message={whatsappLimitMessage} onClose={handleCloseWhatsappLimitModal} />
    </>
  )
}

export default SendInvoiceForm
